import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { ROLES } from "models";
import "modules/WebhooksDashboard/components/WebhooksTestModal/WebhooksTestModal.scss";
import { enumToDropdownOptions } from "utils/common";

import type { TestWebhookFormPayload } from "modules/WebhooksDashboard/components/WebhooksTestModal";

type WebhooksTestFormProps = {
  hasErrors: boolean;
  isUserTrigger: boolean;
  onClose: () => void;
  isPending: boolean;
  onSubmit: (values: TestWebhookFormPayload) => void;
};

const FormHelper = createFormHelper<TestWebhookFormPayload>();

export const WebhooksTestForm = ({ hasErrors, isPending, isUserTrigger, onClose, onSubmit }: WebhooksTestFormProps) => {
  const { t } = useTranslation();
  const { watch, handleSubmit } = FormHelper.useFormContext();

  const [appointmentId, dealerLocationId, woNr, userId] = watch(["appointment_id", "dealer_location_id", "wo_nr", "user_id"]);

  const userRoleOptions = enumToDropdownOptions({ enumObj: ROLES, t });

  return (
    <div className="WebhooksTestModal">
      <Modal.Header className="padding-container">
        <div className="header-container">
          <span className="header-title"> {t("v8_webhook_test", "Webhook Test")}</span>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container">
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required={!isUserTrigger} error={!isUserTrigger && !appointmentId && hasErrors}>
              <label>{t("v8_appointment_id", "Appointment ID")} </label>
              <FormHelper.Field.Input name="appointment_id" inputProps={{ placeholder: t("v8_appointment_id", "Appointment ID"), fluid: true }} />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field required error={!dealerLocationId && hasErrors}>
              <label>{t("v8_dealer_location_id", "Dealer Location ID")}</label>
              <FormHelper.Field.Input name="dealer_location_id" inputProps={{ placeholder: t("v8_dealer_location_id", "Dealer Location ID"), fluid: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required={!isUserTrigger} error={!isUserTrigger && !woNr && hasErrors}>
              <label>{t("v8_work_order_number", "Work Order Number")} </label>
              <FormHelper.Field.Input name="wo_nr" inputProps={{ placeholder: t("v8_work_order_number", "Work Order Number"), fluid: true }} />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field>
              <label>{t("v8_communication_key_for_communication_triggers_only", "Communication Key (for communication triggers only)")} </label>
              <FormHelper.Field.Input
                name="communication_key"
                inputProps={{
                  placeholder: t("v8_communication_key_for_communication_triggers_only", "Communication Key (for communication triggers only)"),
                  fluid: true
                }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field>
              <label>{t("v8_customer_dms_number_for_communication_triggers_only", "Customer DMS Number (for communication triggers only)")} </label>
              <FormHelper.Field.Input
                name="customer_dms_nr"
                inputProps={{
                  placeholder: t("v8_customer_dms_number_for_communication_triggers_only", "Customer DMS Number (for communication triggers only)"),
                  fluid: true
                }}
              />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field>
              <label>{t("v8_customer_type_for_communication_triggers_only", "Customer Type (for communication triggers only)")} </label>
              <FormHelper.Field.Input
                name="customer_type"
                inputProps={{
                  placeholder: t("v8_customer_type_for_communication_triggers_only", "Customer Type (for communication triggers only)"),
                  fluid: true
                }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        {isUserTrigger && (
          <>
            <Grid columns="equal">
              <GridColumn>
                <Form.Field required error={!userId && hasErrors}>
                  <label>{t("v8_user_id", "User ID")} </label>
                  <FormHelper.Field.Input name="user_id" inputProps={{ placeholder: t("v8_user_id", "User ID"), fluid: true }} />
                </Form.Field>
              </GridColumn>

              <GridColumn>
                <Form.Field>
                  <label>{t("v8_role", "Role")} </label>
                  <FormHelper.Field.Dropdown name="role" options={userRoleOptions} dropdownProps={{ placeholder: t("v8_role", "Role"), clearable: true, fluid: true }} />
                </Form.Field>
              </GridColumn>
            </Grid>

            <Grid columns="equal">
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_first_name", "First Name")} </label>
                  <FormHelper.Field.Input name="first_name" inputProps={{ placeholder: t("v8_first_name", "First Name"), fluid: true }} />
                </Form.Field>
              </GridColumn>

              <GridColumn>
                <Form.Field>
                  <label>{t("v8_last_name", "Last Name")} </label>
                  <FormHelper.Field.Input name="last_name" inputProps={{ placeholder: t("v8_last_name", "Last Name"), fluid: true }} />
                </Form.Field>
              </GridColumn>
            </Grid>

            <Grid columns="equal">
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_username", "Username")} </label>
                  <FormHelper.Field.Input name="username" inputProps={{ placeholder: t("v8_username", "Username"), fluid: true }} />
                </Form.Field>
              </GridColumn>

              <GridColumn>
                <Form.Field>
                  <label>{t("v8_email", "Email")} </label>
                  <FormHelper.Field.Input name="email" inputProps={{ placeholder: t("v8_email", "Email"), fluid: true }} />
                </Form.Field>
              </GridColumn>
            </Grid>

            <Grid columns="equal">
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_dealer_id", "Dealer ID")} </label>
                  <FormHelper.Field.Input name="dealer_id" inputProps={{ placeholder: t("v8_dealer_id", "Dealer ID"), fluid: true }} />
                </Form.Field>
              </GridColumn>

              <GridColumn>
                <Form.Field>
                  <label>{t("v8_dealer_name", "Dealer Name")} </label>
                  <FormHelper.Field.Input name="dealer_name" inputProps={{ placeholder: t("v8_dealer_name", "Dealer Name"), fluid: true }} />
                </Form.Field>
              </GridColumn>
            </Grid>

            <Grid columns="equal">
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_location_name", "Location Name")} </label>
                  <FormHelper.Field.Input name="location_name" inputProps={{ placeholder: t("v8_location_name", "Location Name"), fluid: true }} />
                </Form.Field>
              </GridColumn>

              <GridColumn>
                <Form.Field>
                  <label>{t("v8_last_login", "Last Login")} </label>
                  <FormHelper.Field.DatePicker name="last_login" fluid />
                </Form.Field>
              </GridColumn>
            </Grid>

            <Grid columns="equal">
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_web_version", "Web Version")} </label>
                  <FormHelper.Field.Input name="web_version" inputProps={{ placeholder: t("v8_web_version", "Web Version"), fluid: true }} />
                </Form.Field>
              </GridColumn>

              <GridColumn>
                <Form.Field>
                  <label>{t("v8_app_version", "App Version")} </label>
                  <FormHelper.Field.Input name="app_version" inputProps={{ placeholder: t("v8_app_version", "App Version"), fluid: true }} />
                </Form.Field>
              </GridColumn>
            </Grid>

            <Grid columns="equal">
              <GridColumn>
                <FormHelper.Field.Checkbox name="active" label={t("v8_active", "Active")} />
              </GridColumn>

              <GridColumn>
                <FormHelper.Field.Checkbox name="has_leased_tablet" label={t("v8_lease_tablet", "Lease Tablet")} />
              </GridColumn>

              <GridColumn>
                <FormHelper.Field.Checkbox name="is_combi_user" label={t("v8_combi_user", "Combi User")} />
              </GridColumn>

              <GridColumn>
                <FormHelper.Field.Checkbox name="is_counter_tablet_user" label={t("v8_counter_tablet_user", "Counter Tablet User")} />
              </GridColumn>
            </Grid>
          </>
        )}
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="action-buttons-container">
          <Form.Button className="light" onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Form.Button>
          <Form.Button type="submit" color="green" disabled={isPending} loading={isPending} onClick={handleSubmit(d => onSubmit(d))}>
            {t("v8_submit", "Submit")}
            <Icon className="check" />
          </Form.Button>
        </div>
      </Modal.Actions>
    </div>
  );
};
